import { create } from "zustand";

interface AnalyticsState {
  isLoading: boolean;
  isLoadingHashtags: boolean;
  error: string | null;
  campaignAnalyticsData: any;
  creatorAnalyticsData: any;
  selectedTab: "conversion" | "video";
  hashtags: string[];
  setSelectedTab: (tab: "conversion" | "video") => void;
  getCreatorDubAnalytics: (campaign_creator_id: string, value: string, token: string | null) => Promise<any>;
  getCampaignDubAnalytics: (campaign_id: string, token: string | null) => Promise<any>;
  createHashtag: (campaign_id: string, value: string, token: string | null) => Promise<any>;
  getHashtags: (campaign_id: string, token: string | null) => Promise<any>;
  deleteHashtag: (campaign_id: string, value: string, token: string | null) => Promise<any>;
  createUploadedLink: (campaign_creator_id: string, value: string, token: string | null) => Promise<any>;
  deleteUploadedLink: (campaign_creator_id: string, link_id: string, token: string | null) => Promise<any>;
  reset: () => void;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export const useAnalyticsStore = create<AnalyticsState>((set) => ({
  isLoading: false,
  isLoadingHashtags: false,
  error: null,
  creatorAnalyticsData: null,
  campaignAnalyticsData: null,
  selectedTab: "conversion",
  hashtags: [],
  setSelectedTab: (tab) => set({ selectedTab: tab }),

  getCreatorDubAnalytics: async (campaign_creator_id: string, token: string | null) => {
    set({ isLoading: true, error: null });
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/analytics/getCreatorDubAnalytics`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            campaign_creator_id: campaign_creator_id,
          }),
        }
      );
      
      if (!response.ok) throw new Error("Failed to retrieve analytics data");

      const data = await response.json();
      set({ creatorAnalyticsData: data.link_results, isLoading: false });
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
      return null;
    }
  },
  getCampaignDubAnalytics: async (campaign_id: string, token: string | null) => {
    set({ isLoading: true, error: null, campaignAnalyticsData: null });
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/analytics/getCampaignDubAnalytics`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            campaign_id: campaign_id,
          }),
        }
      );
      
      if (!response.ok) throw new Error("Failed to retrieve analytics data");

      const data = await response.json();
      set({ campaignAnalyticsData: data.results, isLoading: false });
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
      return null;
    }
  },

  createHashtag: async (campaign_id: string, value: string, token: string | null) => {
    set({ isLoadingHashtags: true, error: null });
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/analytics/createHashtag`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            campaign_id: campaign_id,
            value: value,
          }),
        }
      );
      
      if (!response.ok) throw new Error("Failed to create hashtag");

      const data = await response.json();
      // add new hashtag to state
      set((state) => ({
        hashtags: [...state.hashtags, value],
      }));
      set({ isLoadingHashtags: false });
      return data;
    } catch (error) {
      set({ error: (error as Error).message, isLoadingHashtags: false });
      return null;
    }
  },

  getHashtags: async (campaign_id: string, token: string | null) => {
    set({ isLoadingHashtags: true, error: null });
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/analytics/getHashtags`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            campaign_id: campaign_id,
          }),
        }
      );
      
      if (!response.ok) throw new Error("Failed to retrieve hashtags");

      const data = await response.json();
      set({ hashtags: data.hashtags, isLoadingHashtags: false });
      return data.hashtags;
    } catch (error) {
      set({ error: (error as Error).message, isLoadingHashtags: false });
      return null;
    }
  },

  deleteHashtag: async (campaign_id: string, value: string, token: string | null) => {
    set({ isLoadingHashtags: true, error: null });
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/analytics/deleteHashtag`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            campaign_id: campaign_id,
            value: value,
          }),
        }
      );
      
      if (!response.ok) throw new Error("Failed to delete hashtag");
      // remove hashtag from state
      set((state) => ({
        hashtags: state.hashtags.filter((hashtag) => hashtag !== value),
      }));
      const data = await response.json();
      set({ isLoadingHashtags: false });
      return data;
    } catch (error) {
      set({ error: (error as Error).message, isLoadingHashtags: false });
      return null;
    }
  },

  createUploadedLink: async (campaign_creator_id: string, value: string, token: string | null) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/analytics/createUploadedLink`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            campaign_creator_id: campaign_creator_id,
            value: value,
          }),
        }
      );
      
      if (!response.ok) throw new Error("Failed to create uploaded link");
      const data = await response.json();
      return data.new_link;
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
      return null;
    }
  },

  deleteUploadedLink: async (campaign_creator_id: string, link_id: string, token: string | null) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/analytics/deleteUploadedLink`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            campaign_creator_id: campaign_creator_id,
            link_id: link_id,
          }),
        }
      );
      
      if (!response.ok) throw new Error("Failed to delete uploaded link");
      const data = await response.json();
      return data;
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
      return null;
    }
  },

  reset: () => set({ isLoading: false, isLoadingHashtags: false, error: null, creatorAnalyticsData: null, campaignAnalyticsData: null, selectedTab: "conversion" }),
}));
